import { useEffect, useRef, useState } from 'react'
import {
    Controller,
    FormProvider,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Link, useNavigate } from '@tanstack/react-router'
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
    ExternalLink,
    GripVertical,
    ImageOff,
    Plus,
    Trash2,
    X,
} from 'lucide-react'
import type { DragEndEvent } from '@dnd-kit/core'

import type { Product } from '@/lib/api/product-api'
import type { ShopAdminGroup } from '@/lib/api/shop-catalog-api'
import type {
    GroupForm as Form,
    VariantRow,
} from '@/components/retail/group-form-model'
import { PageBackButton } from '@/components/common/PageBackButton'
import {
    ErrorText,
    SwitchField,
    TextField,
} from '@/components/retail/form-bits'
import {
    OCOP_LEVELS,
    emptyForm,
    formFromGroup,
    groupFormSchema,
    newKey,
    toInput,
} from '@/components/retail/group-form-model'
import { ChoiceField } from '@/components/retail/home-form-bits'
import { setShopServerErrors } from '@/components/retail/shop-errors'
import { ProductPicker } from '@/components/products/product-picker'
import { ShopImageField } from '@/components/retail/shop-image-field'
import { StorySlugField } from '@/components/retail/story-slug-field'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Combobox } from '@/components/ui/combobox'
import { FieldError } from '@/components/ui/field'
import { RichTextEditor } from '@/components/rich-text'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useDeleteShopGroup,
    useSaveShopGroup,
    useShopCategories,
    useShopGroups,
} from '@/hooks/use-shop-catalog-query'
import { formatDateTime, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { getProductImageURL } from '@/lib/product-image'
import { shopCatalogApi } from '@/lib/api/shop-catalog-api'

const RELATED_MAX = 6
const SHOP_WEB_URL: string =
    import.meta.env.VITE_SHOP_WEB_URL || 'https://chetancuonghaodat.vn'

const BREW_FIELDS = [
    ['tea_amount', () => m.shop_brew_tea_amount()],
    ['water_amount', () => m.shop_brew_water_amount()],
    ['water_temp', () => m.shop_brew_water_temp()],
    ['steep_time', () => m.shop_brew_steep_time()],
    ['infusions', () => m.shop_brew_infusions()],
] as const

interface Props {
    group?: ShopAdminGroup
    canWrite: boolean
}

/**
 * Form Thêm / Sửa nhóm sản phẩm (wireframe ErpNhomSanPhamSua). react-hook-form
 * + ui/field như các form ERP khác: lỗi client (groupFormSchema) và lỗi server
 * (params.field) đều nằm trong formState và hiện dưới đúng ô.
 */
export function GroupForm({ group, canWrite }: Props) {
    const navigate = useNavigate()
    const form = useForm<Form>({
        resolver: zodResolver(groupFormSchema()),
        // Tự đưa tới ô lỗi ở focusFirstInvalid (thứ tự DOM, chỉ cuộn vùng nội
        // dung); để RHF tự focus nữa thì hai lần focus tranh nhau, có thể cuộn
        // cả document.
        shouldFocusError: false,
        defaultValues: group ? formFromGroup(group) : emptyForm(),
    })
    const { control } = form
    const save = useSaveShopGroup()
    const remove = useDeleteShopGroup()
    const categories = useShopCategories().data?.data ?? []
    const otherGroups = (useShopGroups({}).data?.data ?? []).filter(
        (g) => g.id !== group?.id,
    )

    // Lỗi kiểm ở FE có thể nằm xa nút Lưu: báo + đưa tới ô lỗi đầu tiên.
    // react-hook-form gọi onInvalid TRƯỚC khi đẩy errors / submitCount ra state,
    // nên chỉ đánh dấu ở đây; focus trong effect của lần vẽ có submitCount mới
    // (lúc đó aria-invalid đã có trên DOM).
    const formRef = useRef<HTMLFormElement>(null)
    const focusPending = useRef(false)
    const onInvalid = () => {
        // id cố định: bấm Lưu lỗi nhiều lần thì thay toast cũ, không xếp chồng.
        toast.error(m.shop_group_form_invalid(), {
            id: 'shop-group-form-invalid',
        })
        focusPending.current = true
    }
    const { submitCount } = form.formState
    useEffect(() => {
        if (!focusPending.current) return
        focusPending.current = false
        focusFirstInvalid(formRef.current)
    }, [submitCount])

    const submit = form.handleSubmit(async (values) => {
        try {
            const res = await save.mutateAsync({
                id: group?.id,
                input: toInput(values),
            })
            const saved = res.data
            if (!saved) return
            if (!group) {
                void navigate({
                    to: '/retail/groups/$groupId',
                    params: { groupId: saved.id },
                })
            } else {
                form.reset(formFromGroup(saved))
            }
        } catch (err) {
            setShopServerErrors(form.setError, err, m.shop_group_save_failed())
        }
    }, onInvalid)

    return (
        <FormProvider {...form}>
            {/* relative: ô ẩn absolute của Switch / Select (Radix) bám form thay vì
                gốc trang — không thì document có thanh cuộn thứ hai. */}
            <form
                ref={formRef}
                onSubmit={submit}
                noValidate
                className="relative space-y-4 page-pad"
            >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-2">
                        <PageBackButton className="-my-1" />
                        <div className="min-w-0">
                            <h1 className="truncate text-xl font-semibold">
                                {group
                                    ? m.shop_group_edit_title({
                                          name: group.name,
                                      })
                                    : m.shop_group_add()}
                            </h1>
                            {group?.edited_at && (
                                <p className="text-sm text-muted-foreground">
                                    {m.shop_group_edited_at({
                                        at: formatDateTime(group.edited_at),
                                    })}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {group && (
                            <Button variant="outline" size="sm" asChild>
                                <a
                                    href={`${SHOP_WEB_URL}/san-pham/${group.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {m.shop_group_view_on_web()}
                                    <ExternalLink className="ml-1 size-4" />
                                </a>
                            </Button>
                        )}
                        <Button variant="outline" size="sm" asChild>
                            <Link to="/retail/groups">{m.common_cancel()}</Link>
                        </Button>
                        {canWrite && (
                            <Button
                                type="submit"
                                size="sm"
                                disabled={save.isPending}
                            >
                                {save.isPending
                                    ? m.common_saving()
                                    : m.common_save()}
                            </Button>
                        )}
                    </div>
                </div>
                <ErrorText text={form.formState.errors.root?.server?.message} />

                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
                    <div className="min-w-0 space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    {m.shop_group_general()}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <TextField
                                    name="name"
                                    label={`${m.shop_group_name_input()} *`}
                                />
                                <TextField
                                    name="slug"
                                    label={m.shop_slug_label()}
                                    hint={m.shop_slug_hint()}
                                />
                                <Controller
                                    control={control}
                                    name="category_ids"
                                    render={({ field }) => (
                                        <ChipPicker
                                            id="grp-cats"
                                            name="category_ids"
                                            label={m.shop_group_categories_input()}
                                            chips={field.value.map((id) => ({
                                                id,
                                                name:
                                                    categories.find(
                                                        (c) => c.id === id,
                                                    )?.name ?? id,
                                            }))}
                                            options={categories
                                                .filter(
                                                    (c) =>
                                                        !field.value.includes(
                                                            c.id,
                                                        ),
                                                )
                                                .map((c) => ({
                                                    value: c.id,
                                                    label: c.name,
                                                }))}
                                            onAdd={(id) =>
                                                field.onChange([
                                                    ...field.value,
                                                    id,
                                                ])
                                            }
                                            onRemove={(id) =>
                                                field.onChange(
                                                    field.value.filter(
                                                        (x) => x !== id,
                                                    ),
                                                )
                                            }
                                        />
                                    )}
                                />
                                <TextField
                                    name="tagline"
                                    label={m.shop_group_tagline()}
                                    rows={2}
                                />
                                <Controller
                                    control={control}
                                    name="highlights"
                                    render={({ field }) => (
                                        <Highlights
                                            items={field.value}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    <label htmlFor="group-description">
                                        {m.shop_desc_title()}
                                    </label>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <Controller
                                    control={control}
                                    name="description_html"
                                    render={({ field }) => (
                                        <RichTextEditor
                                            id="group-description"
                                            aria-label={m.shop_desc_title()}
                                            value={
                                                field.value ??
                                                group?.description_html ??
                                                ''
                                            }
                                            onChange={field.onChange}
                                            readOnly={!canWrite}
                                        />
                                    )}
                                />
                                <FieldError name="description_html" />
                                <p className="text-xs text-muted-foreground">
                                    {m.shop_desc_note()}
                                </p>
                            </CardContent>
                        </Card>

                        <Controller
                            control={control}
                            name="variants"
                            render={({ field }) => (
                                <Variants
                                    groupId={group?.id}
                                    rows={field.value}
                                    canWrite={canWrite}
                                    onChange={field.onChange}
                                />
                            )}
                        />

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    {m.shop_group_brew()}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                                    {BREW_FIELDS.map(([key, label]) => (
                                        <TextField
                                            key={key}
                                            name={`brew.${key}`}
                                            label={label()}
                                        />
                                    ))}
                                </div>
                                <TextField
                                    name="brew.note"
                                    label={m.shop_brew_note()}
                                    rows={3}
                                />
                                <div className="space-y-1.5">
                                    <Label htmlFor="group-brew-html">
                                        {m.richText_brew_label()}
                                    </Label>
                                    <Controller
                                        control={control}
                                        name="brew_html"
                                        render={({ field }) => (
                                            <RichTextEditor
                                                id="group-brew-html"
                                                aria-label={m.richText_brew_label()}
                                                value={
                                                    field.value ??
                                                    group?.brew_html ??
                                                    ''
                                                }
                                                onChange={field.onChange}
                                                readOnly={!canWrite}
                                            />
                                        )}
                                    />
                                    <FieldError name="brew_html" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    {m.shop_group_links()}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Controller
                                    control={control}
                                    name="related"
                                    render={({ field }) => (
                                        <ChipPicker
                                            id="grp-related"
                                            name="related"
                                            label={m.shop_group_related({
                                                max: String(RELATED_MAX),
                                            })}
                                            chips={field.value}
                                            options={otherGroups
                                                .filter(
                                                    (g) =>
                                                        !field.value.some(
                                                            (r) =>
                                                                r.id === g.id,
                                                        ),
                                                )
                                                .map((g) => ({
                                                    value: g.id,
                                                    label: g.name,
                                                }))}
                                            disabled={
                                                field.value.length >=
                                                RELATED_MAX
                                            }
                                            onAdd={(id) =>
                                                field.onChange([
                                                    ...field.value,
                                                    {
                                                        id,
                                                        name:
                                                            otherGroups.find(
                                                                (g) =>
                                                                    g.id === id,
                                                            )?.name ?? id,
                                                    },
                                                ])
                                            }
                                            onRemove={(id) =>
                                                field.onChange(
                                                    field.value.filter(
                                                        (r) => r.id !== id,
                                                    ),
                                                )
                                            }
                                        />
                                    )}
                                />
                                <StorySlugField />
                            </CardContent>
                        </Card>

                        <OCOPCard />

                        <Card>
                            <CardContent className="pt-6">
                                <details>
                                    <summary className="cursor-pointer text-sm font-medium">
                                        {m.shop_seo_section()}
                                    </summary>
                                    <div className="mt-4 space-y-4">
                                        <TextField
                                            name="seo_title"
                                            label={m.shop_seo_title()}
                                        />
                                        <TextField
                                            name="seo_description"
                                            label={m.shop_seo_description()}
                                        />
                                        <ImageField
                                            name="seo_image_url"
                                            id="grp-seo-img"
                                            label={m.shop_seo_image()}
                                            className="max-w-32"
                                        />
                                    </div>
                                </details>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="min-w-0 space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    {m.shop_group_display()}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <SwitchField
                                    name="show_web"
                                    label={m.shop_show_on_web()}
                                />
                                <SwitchField
                                    name="show_miniapp"
                                    label={m.shop_show_on_miniapp()}
                                />
                                <TextField
                                    name="best_seller_rank"
                                    label={m.shop_group_rank()}
                                />
                                <TextField
                                    name="sort_order"
                                    label={m.shop_group_sort_order()}
                                    hint={m.shop_group_sort_order_hint()}
                                />
                            </CardContent>
                        </Card>
                        <VariantImages />
                        {group && canWrite && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="text-destructive"
                                disabled={remove.isPending}
                                onClick={() => {
                                    // ERP-04: xoá là mất hẳn (cả trang trên
                                    // web / Mini App) — hỏi trước.
                                    if (
                                        !window.confirm(
                                            m.shop_group_delete_confirm({
                                                name: group.name,
                                            }),
                                        )
                                    )
                                        return
                                    remove.mutate(group.id, {
                                        onSuccess: () =>
                                            void navigate({
                                                to: '/retail/groups',
                                            }),
                                    })
                                }}
                            >
                                <Trash2 className="mr-1 size-4" />
                                {m.shop_group_delete()}
                            </Button>
                        )}
                    </div>
                </div>
            </form>
        </FormProvider>
    )
}

/**
 * Đưa lỗi đầu tiên (thứ tự trên trang) ra giữa vùng cuộn: ô aria-invalid thì
 * focus; câu lỗi của cả khối (vd nhóm chưa có loại) thì chỉ cuộn tới.
 */
function focusFirstInvalid(form: HTMLFormElement | null) {
    const el = form?.querySelector<HTMLElement>(
        '[aria-invalid="true"], [data-field-error]',
    )
    if (!el) return
    // scrollIntoView cuộn cả document (thanh trên cùng bị đẩy lệch) →
    // chỉ cuộn vùng cuộn gần nhất chứa ô lỗi, đưa ô ra giữa.
    const box = scrollParent(el)
    const r = el.getBoundingClientRect()
    const top = box ? box.getBoundingClientRect().top : 0
    const height = box ? box.clientHeight : window.innerHeight
    const delta = r.top - top - (height - r.height) / 2
    if (box) box.scrollTop += delta
    else window.scrollBy(0, delta)
    // Câu lỗi không focus được → focus điều khiển đầu tiên ngay sau nó trong
    // cùng khối (vd chưa có loại → ô "Thêm loại").
    const target = el.hasAttribute('data-field-error')
        ? Array.from(
              el.parentElement?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
          ).find(
              (c) =>
                  el.compareDocumentPosition(c) &
                  Node.DOCUMENT_POSITION_FOLLOWING,
          )
        : el
    target?.focus({ preventScroll: true })
}

const FOCUSABLE =
    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function scrollParent(el: HTMLElement): HTMLElement | null {
    for (let p = el.parentElement; p; p = p.parentElement) {
        const y = getComputedStyle(p).overflowY
        if ((y === 'auto' || y === 'scroll') && p.scrollHeight > p.clientHeight)
            return p
    }
    return null
}

/** Nhãn loại gợi ý từ SP: "Gói 100g" (đơn vị + định lượng); vẫn sửa được. */
export function suggestVariantLabel(p: Product): string {
    const unit = p.unit.charAt(0).toUpperCase() + p.unit.slice(1)
    const v = p.weight_value ?? 0
    // Không rõ đơn vị định lượng thì không đoán (500 mà ghi "500kg" là sai).
    if (v <= 0 || !p.weight_unit) return unit
    const kg = p.weight_unit === 'kg'
    const text =
        kg && v < 1
            ? `${Math.round(v * 1000)}g`
            : `${String(v).replace('.', ',')}${p.weight_unit}`
    return `${unit} ${text}`
}

/** Ô ảnh (upload ERP) gắn vào một trường đường dẫn của form. */
function ImageField({
    name,
    id,
    label,
    className,
    hideLabel,
}: {
    name: 'seo_image_url' | 'ocop.certificate_image_url'
    id: string
    label: string
    className?: string
    /** Tiêu đề thẻ đã nói rõ ô này là gì. */
    hideLabel?: boolean
}) {
    const { control } = useFormContext<Form>()
    return (
        <div className="space-y-1.5">
            {!hideLabel && <Label htmlFor={id}>{label}</Label>}
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <ShopImageField
                        id={id}
                        label={label}
                        value={field.value}
                        onChange={field.onChange}
                        className={className}
                    />
                )}
            />
            <FieldError name={name} />
        </div>
    )
}

export function ChipPicker({
    id,
    name,
    label,
    chips,
    options,
    onAdd,
    onRemove,
    disabled,
}: {
    id: string
    /** Đường dẫn trường của form để hiện lỗi (client lẫn server). */
    name: string
    label: string
    chips: { id: string; name: string }[]
    options: { value: string; label: string }[]
    onAdd: (id: string) => void
    onRemove: (id: string) => void
    disabled?: boolean
}) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id}>{label}</Label>
            <div className="flex flex-wrap items-center gap-2">
                {chips.map((c) => (
                    <Badge key={c.id} variant="secondary" className="gap-1">
                        {c.name}
                        <button
                            type="button"
                            aria-label={m.shop_chip_remove_aria({
                                name: c.name,
                            })}
                            onClick={() => onRemove(c.id)}
                        >
                            <X className="size-3" />
                        </button>
                    </Badge>
                ))}
                <div className="w-56">
                    <Combobox
                        id={id}
                        options={options}
                        value=""
                        disabled={disabled}
                        placeholder={m.shop_chip_add()}
                        searchPlaceholder={m.shop_chip_search()}
                        emptyText={m.shop_chip_empty()}
                        onChange={(v) => v && onAdd(v)}
                    />
                </div>
            </div>
            <FieldError name={name} />
        </div>
    )
}

function Highlights({
    items,
    onChange,
}: {
    items: string[]
    onChange: (items: string[]) => void
}) {
    return (
        <div className="space-y-1.5">
            <div>
                <Label>{m.shop_group_highlights()}</Label>
            </div>
            {items.map((h, i) => (
                <div key={i} className="flex gap-2">
                    <Input
                        value={h}
                        aria-label={m.shop_group_highlight_aria({
                            n: String(i + 1),
                        })}
                        onChange={(e) =>
                            onChange(
                                items.map((x, j) =>
                                    j === i ? e.target.value : x,
                                ),
                            )
                        }
                    />
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={m.shop_group_highlight_remove_aria({
                            n: String(i + 1),
                        })}
                        onClick={() =>
                            onChange(items.filter((_, j) => j !== i))
                        }
                    >
                        <X className="size-4" />
                    </Button>
                </div>
            ))}
            <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onChange([...items, ''])}
            >
                <Plus className="mr-1 size-4" />
                {m.shop_group_highlight_add()}
            </Button>
            <FieldError name="highlights" />
        </div>
    )
}

function useDnd() {
    return useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    )
}

function moved<T extends { key: string }>(rows: T[], e: DragEndEvent): T[] {
    if (!e.over || e.active.id === e.over.id) return rows
    const from = rows.findIndex((r) => r.key === e.active.id)
    const to = rows.findIndex((r) => r.key === e.over!.id)
    return arrayMove(rows, from, to)
}

function DragHandle({
    sortable,
    label,
}: {
    sortable: ReturnType<typeof useSortable>
    label: string
}) {
    return (
        <button
            type="button"
            className="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label={label}
            {...sortable.attributes}
            {...sortable.listeners}
        >
            <GripVertical className="size-4" />
        </button>
    )
}

function Variants({
    groupId,
    rows,
    canWrite,
    onChange,
}: {
    /** Nhóm đang sửa (undefined = nhóm mới) — để biết SKU có thuộc nhóm khác. */
    groupId?: string
    rows: VariantRow[]
    canWrite: boolean
    onChange: (rows: VariantRow[]) => void
}) {
    const sensors = useDnd()
    const { getValues } = useFormContext<Form>()
    const [adding, setAdding] = useState(false)
    const [addError, setAddError] = useState<string | null>(null)
    const add = async (p: Product | undefined) => {
        if (!p || rows.some((r) => r.product_id === p.id)) return
        setAdding(true)
        setAddError(null)
        // Ô chọn SP là ô chuẩn của ERP; luật bán lẻ (đang bán + cho bán lẻ, chưa
        // thuộc nhóm khác, giá gồm VAT / giá Online) lấy từ ô SKU, khớp đúng SP.
        const res = await shopCatalogApi.skuPicker(p.sku).catch(() => null)
        setAdding(false)
        const opt = res?.data?.find((o) => o.product_id === p.id)
        if (!res) {
            setAddError(m.error_shop_variant_check_failed({ sku: p.sku }))
            return
        }
        if (!opt) {
            setAddError(m.error_shop_variant_not_sellable({ sku: p.sku }))
            return
        }
        if (opt.group_id && opt.group_id !== groupId) {
            setAddError(
                m.error_shop_variant_in_other_group({
                    sku: p.sku,
                    group: opt.group_name ?? '',
                }),
            )
            return
        }
        const image = p.images?.find((i) => i.is_primary) ?? p.images?.[0]
        // Đọc dòng mới nhất: trong lúc chờ tra SKU người dùng có thể đã sửa / kéo.
        const latest = getValues('variants')
        if (latest.some((r) => r.product_id === p.id)) return
        onChange([
            ...latest,
            {
                key: newKey(),
                product_id: p.id,
                sku: p.sku,
                product_name: p.name,
                unit: p.unit,
                label: suggestVariantLabel(p),
                show_web: true,
                show_miniapp: true,
                in_stock: true,
                stock_available: opt.stock_available,
                price_incl_vat: opt.price_incl_vat,
                has_online_price: false,
                sellable: true,
                image_url: image?.image_url ?? null,
                thumb_url: image ? `${image.image_url}.thumb.jpg` : null,
            },
        ])
    }
    const patch = (key: string, p: Partial<VariantRow>) =>
        onChange(rows.map((r) => (r.key === key ? { ...r, ...p } : r)))

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">
                    {m.shop_group_variants({ n: String(rows.length) })}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="overflow-x-auto">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(e) => onChange(moved(rows, e))}
                    >
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-8" />
                                    <TableHead>
                                        {m.shop_variant_sku()}
                                    </TableHead>
                                    <TableHead>{m.shop_col_label()}</TableHead>
                                    <TableHead className="text-center">
                                        {m.shop_col_web()}
                                    </TableHead>
                                    <TableHead className="text-center">
                                        {m.shop_col_miniapp()}
                                    </TableHead>
                                    <TableHead className="text-center">
                                        {m.shop_col_in_stock()}
                                    </TableHead>
                                    <TableHead className="w-8" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <SortableContext
                                    items={rows.map((r) => r.key)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {rows.map((r, i) => (
                                        <VariantLine
                                            key={r.key}
                                            row={r}
                                            index={i}
                                            onPatch={(p) => patch(r.key, p)}
                                            onRemove={() =>
                                                onChange(
                                                    rows.filter(
                                                        (x) => x.key !== r.key,
                                                    ),
                                                )
                                            }
                                        />
                                    ))}
                                </SortableContext>
                            </TableBody>
                        </Table>
                    </DndContext>
                </div>
                <FieldError name="variants" />
                {canWrite && (
                    <ProductPicker
                        id="variant-add"
                        className="w-full max-w-md"
                        value=""
                        placeholder={m.shop_variant_add()}
                        excludeProductIds={rows.map((r) => r.product_id)}
                        disabled={adding}
                        onChange={(_, p) => void add(p)}
                    />
                )}
                <ErrorText text={addError} />
                <p className="text-xs text-muted-foreground">
                    {m.shop_variant_note()}
                </p>
            </CardContent>
        </Card>
    )
}

function VariantLine({
    row: r,
    index,
    onPatch,
    onRemove,
}: {
    row: VariantRow
    index: number
    onPatch: (p: Partial<VariantRow>) => void
    onRemove: () => void
}) {
    const sortable = useSortable({ id: r.key })
    const { getFieldState, formState } = useFormContext<Form>()
    const labelInvalid = !!getFieldState(`variants.${index}.label`, formState)
        .error
    return (
        <TableRow
            ref={sortable.setNodeRef}
            style={{
                transform: CSS.Transform.toString(sortable.transform),
                transition: sortable.transition,
            }}
        >
            <TableCell>
                <DragHandle
                    sortable={sortable}
                    label={m.shop_drag_aria({ name: r.sku })}
                />
            </TableCell>
            <TableCell>
                <div className="font-mono text-sm">{r.sku}</div>
                <div className="text-xs text-muted-foreground">
                    {r.product_name}
                    {!r.sellable && ` · ${m.shop_variant_not_sellable()}`}
                </div>
                <div className="text-xs tabular-nums">
                    {r.stock_available > 0
                        ? m.shop_variant_stock_left({
                              n: r.stock_available.toLocaleString('vi-VN'),
                          })
                        : m.shop_variant_stock_none()}
                    {' · '}
                    {formatVnd(r.price_incl_vat)}
                </div>
                <FieldError name={`variants.${index}.product_id`} />
            </TableCell>
            <TableCell className="min-w-32">
                <Input
                    value={r.label}
                    aria-label={m.shop_variant_label_aria({ sku: r.sku })}
                    aria-invalid={labelInvalid}
                    onChange={(e) => onPatch({ label: e.target.value })}
                />
                <FieldError name={`variants.${index}.label`} />
            </TableCell>
            <TableCell className="text-center">
                <Switch
                    checked={r.show_web}
                    aria-label={m.shop_toggle_web_aria({ name: r.sku })}
                    onCheckedChange={(v) => onPatch({ show_web: v })}
                />
            </TableCell>
            <TableCell className="text-center">
                <Switch
                    checked={r.show_miniapp}
                    aria-label={m.shop_toggle_miniapp_aria({ name: r.sku })}
                    onCheckedChange={(v) => onPatch({ show_miniapp: v })}
                />
            </TableCell>
            <TableCell className="text-center">
                <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={r.in_stock}
                    aria-label={m.shop_variant_in_stock_aria({ sku: r.sku })}
                    onChange={(e) => onPatch({ in_stock: e.target.checked })}
                />
            </TableCell>
            <TableCell>
                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={m.shop_variant_remove_aria({ sku: r.sku })}
                    onClick={onRemove}
                >
                    <X className="size-4" />
                </Button>
            </TableCell>
        </TableRow>
    )
}

/**
 * Nhóm không có ảnh riêng: web / Mini App hiện ảnh chính sản phẩm ERP của loại
 * đang chọn. Xem trước chỉ đọc; loại chưa có ảnh mở trang sản phẩm để "Thiết kế ảnh".
 */
function VariantImages() {
    const { control } = useFormContext<Form>()
    const rows = useWatch({ control, name: 'variants' })
    // Cùng luật server (public.go), tính riêng từng kênh: loại hiện trên kênh còn
    // hàng đầu tiên, hết cả thì loại hiện đầu tiên.
    const defaultOn = (shown: (r: VariantRow) => boolean) => {
        const on = rows.filter(shown)
        return (on.find((r) => r.in_stock) ?? on[0])?.key
    }
    const webKey = defaultOn((r) => r.show_web)
    const miniKey = defaultOn((r) => r.show_miniapp)
    const defaultLabel = (key: string) =>
        key === webKey && key === miniKey
            ? m.shop_group_image_default()
            : key === webKey
              ? m.shop_group_image_default_web()
              : key === miniKey
                ? m.shop_group_image_default_miniapp()
                : null
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">
                    {m.shop_group_images_title()}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        {m.shop_group_images_empty()}
                    </p>
                ) : (
                    <ul
                        className="grid grid-cols-2 gap-3"
                        aria-label={m.shop_group_images_title()}
                    >
                        {rows.map((r) => (
                            <li
                                key={r.key}
                                className="relative min-w-0 space-y-1"
                            >
                                {defaultLabel(r.key) && (
                                    <Badge className="absolute top-1.5 left-1.5 z-10">
                                        {defaultLabel(r.key)}
                                    </Badge>
                                )}
                                {r.image_url ? (
                                    <img
                                        src={getProductImageURL(r.image_url)}
                                        alt={r.label || r.product_name}
                                        className="aspect-square w-full rounded-md border object-cover"
                                    />
                                ) : (
                                    <Link
                                        to="/products/$productId"
                                        params={{ productId: r.product_id }}
                                        target="_blank"
                                        className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed bg-muted/40 p-2 text-center text-xs text-muted-foreground hover:border-primary hover:text-primary"
                                    >
                                        <ImageOff className="size-6 stroke-1" />
                                        {m.shop_group_image_missing()}
                                        <span className="font-medium underline underline-offset-2">
                                            {m.shop_group_image_design()}
                                        </span>
                                    </Link>
                                )}
                                <p
                                    className="truncate text-xs"
                                    title={`${r.label || r.product_name} · ${r.sku}`}
                                >
                                    {r.label || r.product_name}
                                    <span className="ml-1 font-mono text-muted-foreground">
                                        {r.sku}
                                    </span>
                                </p>
                            </li>
                        ))}
                    </ul>
                )}
                <p className="text-xs text-muted-foreground">
                    {m.shop_group_images_hint()}
                </p>
            </CardContent>
        </Card>
    )
}

function OCOPCard() {
    const { control } = useFormContext<Form>()
    const visible = useWatch({ control, name: 'ocop.visible' })
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">{m.shop_ocop()}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <SwitchField
                    name="ocop.visible"
                    label={m.shop_ocop_visible()}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="ocop.stars">
                            {m.shop_ocop_stars()}
                        </Label>
                        <Controller
                            control={control}
                            name="ocop.stars"
                            render={({ field }) => (
                                <Select
                                    value={field.value || 'none'}
                                    onValueChange={(v) =>
                                        field.onChange(v === 'none' ? '' : v)
                                    }
                                >
                                    <SelectTrigger id="ocop.stars">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">—</SelectItem>
                                        {[3, 4, 5].map((n) => (
                                            <SelectItem
                                                key={n}
                                                value={String(n)}
                                            >
                                                {m.shop_ocop_stars_n({
                                                    n: String(n),
                                                })}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        />
                        <FieldError name="ocop.stars" />
                    </div>
                    {/* Web / Mini App chỉ hiểu 2 cấp này (chung khối OCOP trang chủ).
                        Đang hiện huy hiệu thì bỏ "—": web coi cấp trống là cấp
                        tỉnh, nên không cho chọn cái sẽ bị báo lỗi khi lưu. */}
                    <ChoiceField
                        name="ocop.level"
                        label={m.shop_ocop_level()}
                        noneLabel={visible ? undefined : '—'}
                        placeholder={m.shop_ocop_level_placeholder()}
                        options={OCOP_LEVELS.map((value) => ({
                            value,
                            label:
                                value === 'national'
                                    ? m.shop_home_ocop_level_national()
                                    : m.shop_home_ocop_level_provincial(),
                        }))}
                    />
                    <TextField name="ocop.year" label={m.shop_ocop_year()} />
                    <TextField
                        name="ocop.decision"
                        label={m.shop_ocop_decision()}
                    />
                    <DateField
                        name="ocop.valid_until"
                        label={m.shop_ocop_until()}
                    />
                    <TextField
                        name="ocop.certificate_title"
                        label={m.shop_ocop_cert_title()}
                    />
                </div>
                <ImageField
                    name="ocop.certificate_image_url"
                    id="ocop-cert"
                    label={m.shop_ocop_cert_image()}
                    className="max-w-32"
                />
            </CardContent>
        </Card>
    )
}

function DateField({
    name,
    label,
}: {
    name: 'ocop.valid_until'
    label: string
}) {
    const { register } = useFormContext<Form>()
    return (
        <div className="space-y-1.5">
            <Label htmlFor={name}>{label}</Label>
            <Input id={name} type="date" {...register(name)} />
            <FieldError name={name} />
        </div>
    )
}
